import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'CartBulkActionDrawer' }

import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import moment from 'moment-timezone'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, pickerDateFormat as toPickerDateFormat } from '@/plugins/dates'

type ActionId = 'resolve-errors' | 'delete-abandoned'
type ActionOption = { id: ActionId; label: string }


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  show: { type: Boolean, default: false },
  // Carts page tab the drawer opened from: 0 abandoned carts, 2 cart errors.
  tab: { type: Number, default: 0 },
},
  emits: ['update:show', 'close', 'done'],
  setup(__props, { emit }) {

const props = __props

// Criteria-driven bulk actions on the carts page. The action select is filtered by the tab
// the drawer opened from; each action keeps its own criteria, endpoint and copy.
//   'resolve-errors':    PUT    /companies/{id}/groups/{grid}/carts/errors  body { resolved, error_types, from, to, preview }
//   'delete-abandoned':  DELETE /companies/{id}/groups/{grid}/carts         query { from, to, preview }
// Both return { count }. With preview the count is what would change; without it, what did.



function readGroup() {
  try { return JSON.parse(localStorage.getItem('group') || '{}') } catch (e) { return {} }
}
const group = readGroup()
const companyId = localStorage.getItem('companyId')
const groupTz: string = group.tz_name || moment.tz.guess()
const pickerDateFormat = toPickerDateFormat(group.date_format)
const dayOfWeekPrefix = group.show_day_of_week ? 'ddd, ' : ''
const firstDayOfWeek: number = Number(group.first_day_of_week) || 0
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${group.id}/carts`

const ACTIONS_BY_TAB: Record<number, ActionOption[]> = {
  0: [{ id: 'delete-abandoned', label: 'Delete carts' }],
  2: [{ id: 'resolve-errors', label: 'Mark errors as resolved' }],
}
const actionOptions = computed<ActionOption[]>(() => ACTIONS_BY_TAB[props.tab] || [])
const action = ref<ActionOption>(actionOptions.value[0] || { id: 'resolve-errors', label: 'Mark errors as resolved' })
const isResolve = computed(() => action.value.id === 'resolve-errors')

const copy = computed(() => isResolve.value
  ? { rangeTitle: 'Error date range', submit: 'Resolve errors', noun: ['error', 'errors'] }
  : { rangeTitle: 'Last activity', submit: 'Delete carts', noun: ['cart', 'carts'] })

const errorTypeOptions = [
  { value: 'resource', label: 'Resource' },
  { value: 'payment', label: 'Payment' },
  { value: 'validation', label: 'Validation' },
  { value: 'promo_code', label: 'Promo code' },
  { value: 'gift_card', label: 'Gift card' },
  { value: 'customer', label: 'Customer' },
  { value: 'add_on', label: 'Add on' },
  { value: 'booking_slot', label: 'Booking slot' },
  { value: 'generic', label: 'Generic' },
]

function defaultForm() {
  return {
    errorTypes: errorTypeOptions.map(o => o.value),
    from: null as Date | null,
    to: new Date() as Date | null,
  }
}
const form = ref(defaultForm())
const fieldErrors = reactive({ errorTypes: '', from: '', to: '' })
const working = ref(false)
const previewCount = ref(0)

const alert = reactive({
  show: false, variant: 'warning', title: '', message: '',
  showCancel: false, confirmText: 'OK', confirmVariant: 'primary',
  mode: 'info' as 'info' | 'confirm' | 'done',
})

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

// The picker gives a local calendar day; the lambdas compare UTC datetimes.
function dayStartUtc(d: Date) {
  return moment.tz(moment(d).format('YYYY-MM-DD'), 'YYYY-MM-DD', groupTz).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
}
function dayEndUtc(d: Date) {
  return moment.tz(moment(d).format('YYYY-MM-DD'), 'YYYY-MM-DD', groupTz).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
}

function clearErrors() {
  fieldErrors.errorTypes = ''
  fieldErrors.from = ''
  fieldErrors.to = ''
}

function validate(): boolean {
  clearErrors()
  if (isResolve.value && form.value.errorTypes.length === 0) fieldErrors.errorTypes = 'Choose at least one error type'
  if (!form.value.to) fieldErrors.to = 'Choose an end date'
  if (form.value.from && form.value.to && moment(form.value.from).isAfter(form.value.to, 'day')) {
    fieldErrors.from = 'From date cannot be after the to date'
  }
  return !fieldErrors.errorTypes && !fieldErrors.from && !fieldErrors.to
}

function range() {
  return {
    from: form.value.from ? dayStartUtc(form.value.from) : null,
    to: dayEndUtc(form.value.to as Date),
  }
}

async function request(preview: boolean) {
  const config = await authConfig()
  const r = range()
  if (isResolve.value) {
    const body = { resolved: true, error_types: form.value.errorTypes, from: r.from, to: r.to, preview, confirm: !preview }
    return axios.put(`${baseUrl}/errors`, body, config)
  }
  const params: Record<string, string> = { to: r.to }
  if (r.from) params.from = r.from
  if (preview) params.preview = '1'
  else params.confirm = '1'
  return axios.delete(baseUrl, { ...config, params })
}

const typeLabels = computed(() =>
  errorTypeOptions.filter(o => form.value.errorTypes.includes(o.value)).map(o => o.label.toLowerCase()).join(', ')
)

// Reads as one sentence: "52 resource errors logged on or before Saturday, September 19, 2026"
function criteriaText(n: number) {
  const r = range()
  const to = formatGroupDate(r.to)
  const span = r.from ? `between ${formatGroupDate(r.from)} and ${to}` : `on or before ${to}`
  if (isResolve.value) {
    const all = form.value.errorTypes.length === errorTypeOptions.length
    const kind = all ? '' : `${typeLabels.value} `
    return `${n} ${kind}${n === 1 ? 'error' : 'errors'} logged ${span}`
  }
  return `${n} abandoned ${n === 1 ? 'cart' : 'carts'} last active ${span}`
}

function nounFor(n: number) {
  return n === 1 ? copy.value.noun[0] : copy.value.noun[1]
}

// Only a lambda's own { error } text is shown to the user. Gateway or plain-string
// bodies fall through to the generic copy.
function errorText(err: any): string {
  const data = err?.response?.data
  if (!data) return ''
  if (typeof data === 'string') {
    try { return JSON.parse(data).error || '' } catch (e) { return '' }
  }
  return data.error || ''
}

function showFailure(err: any) {
  const status = err?.response?.status
  alert.mode = 'info'
  alert.variant = 'danger'
  alert.showCancel = false
  alert.confirmText = 'Close'
  alert.confirmVariant = 'primary'
  alert.title = status === 401 ? 'Not allowed' : 'Something went wrong'
  alert.message = status === 401
    ? 'You do not have permission for this action. Ask your admin about your site permissions.'
    : (errorText(err) || 'The action could not be completed. Please try again in a moment.')
  alert.show = true
}

async function preview() {
  if (!validate()) return
  working.value = true
  try {
    const response = await request(true)
    previewCount.value = Number(response.data.count) || 0
    const n = previewCount.value
    if (n === 0) {
      alert.mode = 'info'
      alert.variant = 'info'
      alert.showCancel = false
      alert.confirmText = 'Close'
      alert.confirmVariant = 'primary'
      alert.title = isResolve.value ? 'Nothing to resolve' : 'Nothing to delete'
      alert.message = `There are ${criteriaText(0).replace(/^0 /, 'no ')}.`
    } else {
      alert.mode = 'confirm'
      alert.variant = isResolve.value ? 'warning' : 'danger'
      alert.showCancel = true
      alert.confirmVariant = isResolve.value ? 'primary' : 'danger'
      alert.confirmText = `${isResolve.value ? 'Resolve' : 'Delete'} ${n} ${nounFor(n)}`
      alert.title = isResolve.value ? 'Confirm bulk resolve' : 'Confirm bulk delete'
      alert.message = isResolve.value
        ? `Mark ${criteriaText(n)} as resolved?`
        : `Permanently delete ${criteriaText(n)}? This cannot be undone.`
    }
    alert.show = true
  } catch (err) {
    showFailure(err)
  } finally {
    working.value = false
  }
}

async function apply() {
  working.value = true
  try {
    const response = await request(false)
    const n = Number(response.data.count) || 0
    alert.mode = 'done'
    alert.variant = 'success'
    alert.showCancel = false
    alert.confirmText = 'Close'
    alert.confirmVariant = 'primary'
    alert.title = isResolve.value ? 'Errors resolved' : 'Carts deleted'
    alert.message = isResolve.value
      ? `${n} ${n === 1 ? 'error was' : 'errors were'} marked as resolved.`
      : `${n} ${n === 1 ? 'cart was' : 'carts were'} deleted.`
    alert.show = true
  } catch (err) {
    showFailure(err)
  } finally {
    working.value = false
  }
}

function onAlertConfirm() {
  if (alert.mode === 'confirm') {
    alert.show = false
    apply()
    return
  }
  alert.show = false
  if (alert.mode === 'done') {
    emit('done')
    onClose()
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && !alert.show) onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    action.value = actionOptions.value[0] || action.value
    form.value = defaultForm()
    clearErrors()
    alert.show = false
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
}, { immediate: true })

watch(action, () => { clearErrors() })

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})

return { __sfc: true,props, emit, readGroup, group, companyId, groupTz, pickerDateFormat, dayOfWeekPrefix, firstDayOfWeek, baseUrl, ACTIONS_BY_TAB, actionOptions, action, isResolve, copy, errorTypeOptions, defaultForm, form, fieldErrors, working, previewCount, alert, authConfig, dayStartUtc, dayEndUtc, clearErrors, validate, range, request, typeLabels, criteriaText, nounFor, errorText, showFailure, preview, apply, onAlertConfirm, onClose, onEscKey }
}

})